import React, { useState, useEffect } from 'react';
import { getStaffAttendance, saveStaffAttendance, getStaffAttendanceReport } from '../storage';
import { Avatar, Btn, Card, NoData, TblWrap, toast } from '../ui.jsx';

function StaffAttendancePage({ db, activeSessionId }) {
  const [tab, setTab] = useState(0); // 0=daily entry, 1=report
  const [dt, setDt] = useState(new Date().toISOString().split('T')[0]);
  const [attMap, setAttMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [report, setReport] = useState([]);
  const [repLoading, setRepLoading] = useState(false);
  const [filterTid, setFilterTid] = useState('');

  const staff = db.teachers || [];

  // Load attendance for selected date
  useEffect(() => {
    if (!activeSessionId || tab !== 0) return;
    setLoaded(false);
    getStaffAttendance(activeSessionId, dt).then(data => {
      const m = {};
      staff.forEach(t => {
        const rec = (data || []).find(r => r.tid === (t.tid || t.id));
        m[t.tid || t.id] = rec ? { ...rec } : { status: 'Present', type: 'Full Day', inTime: '', outTime: '' };
      });
      setAttMap(m);
      setLoaded(true);
    }).catch(() => {
      const m = {};
      staff.forEach(t => { m[t.tid || t.id] = { status: 'Present', type: 'Full Day', inTime: '', outTime: '' }; });
      setAttMap(m);
      setLoaded(true);
    });
  }, [dt, activeSessionId, tab]);

  // Load report
  useEffect(() => {
    if (tab !== 1 || !activeSessionId) return;
    setRepLoading(true);
    getStaffAttendanceReport(activeSessionId, filterTid || undefined).then(data => {
      setReport(Array.isArray(data) ? data : []);
      setRepLoading(false);
    }).catch(() => { setReport([]); setRepLoading(false); });
  }, [tab, activeSessionId, filterTid]);

  const setField = (tid, field, val) => setAttMap(m => ({ ...m, [tid]: { ...m[tid], [field]: val } }));

  const saveAtt = async () => {
    if (!activeSessionId) { toast('No active session', 'err'); return; }
    setSaving(true);
    const records = staff.map(t => {
      const tid = t.tid || t.id;
      return { tid, name: `${t.fn || ''} ${t.ln || ''}`.trim(), ...(attMap[tid] || { status: 'Present', type: 'Full Day', inTime: '', outTime: '' }) };
    });
    try {
      await saveStaffAttendance(activeSessionId, dt, records);
      toast('Staff attendance saved');
    } catch (e) {
      toast('Failed to save: ' + e.message, 'err');
    }
    setSaving(false);
  };

  const markAll = (status) => {
    const m = {};
    staff.forEach(t => { const tid = t.tid || t.id; m[tid] = { ...(attMap[tid] || {}), status }; });
    setAttMap(m);
  };

  const statusColor = s => s === 'Present' ? '#059669' : s === 'Absent' ? '#ef4444' : s === 'Half Day' ? '#d97706' : '#6366f1';
  const statusBg = s => s === 'Present' ? '#d1fae5' : s === 'Absent' ? '#fee2e2' : s === 'Half Day' ? '#fef3c7' : '#ede9fe';

  // Summary counts
  const counts = { Present: 0, Absent: 0, 'Half Day': 0, Leave: 0 };
  Object.values(attMap).forEach(r => { if (counts[r?.status] !== undefined) counts[r.status]++; });

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-on-surface-variant text-xs font-bold uppercase tracking-widest mb-1">HR Management</p>
          <h1 className="text-4xl font-extrabold text-primary font-headline tracking-tight">Staff Attendance</h1>
        </div>
        <div className="flex bg-surface-container-low p-1 rounded-xl">
          {['Daily Entry', 'Report'].map((l, i) => (
            <button key={i} onClick={() => setTab(i)}
              className={`px-6 py-2 rounded-lg font-bold text-sm transition-all border-0 cursor-pointer ${tab === i ? 'bg-surface-container-lowest text-primary shadow-sm' : 'text-on-surface-variant hover:text-primary bg-transparent'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {tab === 0 && (
        <div>
          {/* Date + quick actions */}
          <div className="flex flex-wrap gap-3 items-center mb-6">
            <input type="date" value={dt} onChange={e => setDt(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-outline-variant bg-surface-container-lowest text-on-surface font-medium text-sm focus:outline-none focus:ring-2 focus:ring-primary/20" />
            <button onClick={() => markAll('Present')}
              className="px-4 py-2 rounded-xl bg-emerald-50 text-emerald-700 font-bold text-sm border-0 cursor-pointer hover:bg-emerald-100 transition-colors">
              ✓ All Present
            </button>
            <button onClick={() => markAll('Absent')}
              className="px-4 py-2 rounded-xl bg-red-50 text-red-600 font-bold text-sm border-0 cursor-pointer hover:bg-red-100 transition-colors">
              ✗ All Absent
            </button>
            <button onClick={saveAtt} disabled={saving || !loaded}
              className="ml-auto px-6 py-2.5 rounded-xl bg-primary text-white font-bold text-sm border-0 cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Attendance'}
            </button>
          </div>

          {/* Summary pills */}
          <div className="flex flex-wrap gap-3 mb-6">
            {Object.entries(counts).map(([s, n]) => (
              <div key={s} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold" style={{ background: statusBg(s), color: statusColor(s) }}>
                <span>{s}</span><span className="text-lg leading-none">{n}</span>
              </div>
            ))}
          </div>

          {/* Staff list */}
          {!loaded ? (
            <div className="text-center py-16 text-on-surface-variant">Loading…</div>
          ) : staff.length === 0 ? (
            <div className="text-center py-16 text-on-surface-variant">No staff members found. Add staff first.</div>
          ) : (
            <div className="bg-surface-container-lowest rounded-2xl overflow-hidden shadow-sm">
              <TblWrap>
                <table>
                  <thead>
                    <tr>
                      <th className="px-5 py-4 text-left">Staff Member</th>
                      <th className="px-5 py-4 text-left">Status</th>
                      <th className="px-5 py-4 text-left">Type</th>
                      <th className="px-5 py-4 text-left">In Time</th>
                      <th className="px-5 py-4 text-left">Out Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map((t, i) => {
                      const tid = t.tid || t.id;
                      const rec = attMap[tid] || { status: 'Present', type: 'Full Day', inTime: '', outTime: '' };
                      return (
                        <tr key={tid} className="border-t border-surface-container-low hover:bg-surface-container-low/50 transition-colors">
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <Avatar name={`${t.fn || ''} ${t.ln || ''}`} idx={i} />
                              <div>
                                <div className="font-bold text-on-surface">{t.fn} {t.ln}</div>
                                <div className="text-xs text-outline">{t.desig || t.su || t.staffType || '—'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex gap-1 flex-wrap">
                              {['Present', 'Absent', 'Half Day', 'Leave'].map(s => (
                                <button key={s} onClick={() => setField(tid, 'status', s)}
                                  className="px-3 py-1 rounded-full text-xs font-bold border-0 cursor-pointer transition-all"
                                  style={{ background: rec.status === s ? statusBg(s) : '#f1f5f9', color: rec.status === s ? statusColor(s) : '#94a3b8', fontWeight: rec.status === s ? 800 : 600 }}>
                                  {s}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <select value={rec.type || 'Full Day'} onChange={e => setField(tid, 'type', e.target.value)}
                              className="px-3 py-1.5 rounded-lg border border-outline-variant bg-surface-container-lowest text-on-surface text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/20">
                              <option>Full Day</option>
                              <option>Half Day</option>
                            </select>
                          </td>
                          <td className="px-5 py-4">
                            <input type="time" value={rec.inTime || ''} onChange={e => setField(tid, 'inTime', e.target.value)}
                              className="px-3 py-1.5 rounded-lg border border-outline-variant bg-surface-container-lowest text-on-surface text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/20" />
                          </td>
                          <td className="px-5 py-4">
                            <input type="time" value={rec.outTime || ''} onChange={e => setField(tid, 'outTime', e.target.value)}
                              className="px-3 py-1.5 rounded-lg border border-outline-variant bg-surface-container-lowest text-on-surface text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/20" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TblWrap>
            </div>
          )}
        </div>
      )}

      {tab === 1 && (
        <div>
          <div className="flex flex-wrap gap-3 items-center mb-6">
            <select value={filterTid} onChange={e => setFilterTid(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-outline-variant bg-surface-container-lowest text-on-surface font-medium text-sm focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option value="">All Staff</option>
              {staff.map(t => <option key={t.tid || t.id} value={t.tid || t.id}>{t.fn} {t.ln}</option>)}
            </select>
          </div>
          {repLoading ? (
            <div className="text-center py-16 text-on-surface-variant">Loading report…</div>
          ) : report.length === 0 ? (
            <div className="text-center py-16 text-on-surface-variant">No attendance records found.</div>
          ) : (
            <div className="bg-surface-container-lowest rounded-2xl overflow-hidden shadow-sm">
              <TblWrap>
                <table>
                  <thead>
                    <tr>
                      <th className="px-5 py-4 text-left">Date</th>
                      <th className="px-5 py-4 text-left">Staff</th>
                      <th className="px-5 py-4 text-left">Status</th>
                      <th className="px-5 py-4 text-left">Type</th>
                      <th className="px-5 py-4 text-left">In Time</th>
                      <th className="px-5 py-4 text-left">Out Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.map((r, i) => (
                      <tr key={i} className="border-t border-surface-container-low hover:bg-surface-container-low/50 transition-colors">
                        <td className="px-5 py-4 font-medium text-on-surface">{r.date}</td>
                        <td className="px-5 py-4 text-on-surface">{r.name || r.tid}</td>
                        <td className="px-5 py-4">
                          <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: statusBg(r.status), color: statusColor(r.status) }}>{r.status}</span>
                        </td>
                        <td className="px-5 py-4 text-on-surface-variant">{r.type || '—'}</td>
                        <td className="px-5 py-4 text-on-surface-variant">{r.inTime || '—'}</td>
                        <td className="px-5 py-4 text-on-surface-variant">{r.outTime || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TblWrap>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default StaffAttendancePage;
